import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/TranslationContext";
import { hasPermission } from "@/lib/permissions";
import { SeverityBadge } from "@/components/SeverityBadge";
import { toast } from "sonner";
import { ShieldCheck, Newspaper, RefreshCw, Radar } from "lucide-react";

const ago = (iso) => {
  if (!iso) return "never";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export const LiveIntel = ({ user }) => {
  const { tx } = useI18n();
  const isAdmin = user?.role === "admin";
  const canReadSignals = hasPermission(user, "signal.read");
  const [wt, setWt] = useState(null);
  const [feed, setFeed] = useState([]);
  const [history, setHistory] = useState([]);
  const [trend, setTrend] = useState([]);
  const [running, setRunning] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [showOrig, setShowOrig] = useState(() => new Set());

  const toggleOrig = (id) =>
    setShowOrig((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const loadWatchtower = useCallback(() => {
    api.get("/watchtower/status").then(({ data }) => setWt(data)).catch(() => {});
    api.get("/watchtower/history?limit=6").then(({ data }) => setHistory(data.runs)).catch(() => {});
  }, []);

  const loadFeed = useCallback(() => {
    if (!canReadSignals) return;
    api
      .get("/signals?source=gdelt&limit=6")
      .then(({ data }) => setFeed(data.items))
      .catch(() => {});
    api
      .get("/intel/gdelt-trend?days=14")
      .then(({ data }) => setTrend(data.series))
      .catch(() => {});
  }, [canReadSignals]);

  useEffect(() => {
    loadWatchtower();
    loadFeed();
  }, [loadWatchtower, loadFeed]);

  const runWatchtower = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/watchtower/run");
      toast.success(`Watchtower screened ${data.screened} suppliers · ${data.hits} hit(s)`);
      loadWatchtower();
    } catch {
      toast.error("Watchtower run failed");
    } finally {
      setRunning(false);
    }
  };

  const pullGdelt = async () => {
    setPulling(true);
    try {
      const { data } = await api.post("/signals/ingest-gdelt?max_records=30&timespan=1d");
      if (data.error) toast.info("GDELT is rate-limited — try again shortly.");
      else toast.success(`Ingested ${data.ingested} GDELT article(s)`);
      loadFeed();
    } catch {
      toast.error("GDELT pull failed");
    } finally {
      setPulling(false);
    }
  };

  return (
    <section className="mt-12" data-testid="live-intel-section">
      <h2 className="ek-eyebrow">Live intelligence</h2>
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        {/* Sanctions Watchtower */}
        <div className="border border-black/5 bg-white p-5" data-testid="watchtower-card">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-1.5 font-display text-base font-semibold text-ek-ink">
              <ShieldCheck size={16} className="text-ek-purple" /> Sanctions Watchtower
            </h3>
            {isAdmin && (
              <button
                onClick={runWatchtower}
                disabled={running}
                data-testid="watchtower-run-button"
                className="flex items-center gap-1 rounded-sm border border-ek-purple/30 px-2.5 py-1 text-xs font-semibold text-ek-purple transition-colors hover:bg-ek-purple/5 disabled:opacity-50"
              >
                <RefreshCw size={11} className={running ? "animate-spin" : ""} /> Run now
              </button>
            )}
          </div>
          <p className="mt-1 text-xs text-ek-ink/65">
            Automated supplier re-screening every 6 hours.
          </p>
          <div className="mt-4 grid grid-cols-3 gap-3">
            <Stat label="Screened" value={wt?.screened ?? "—"} />
            <Stat label="Sanctions hits" value={wt?.hits ?? 0} accent={wt?.hits > 0} />
            <Stat label="Last run" value={ago(wt?.last_run)} small />
          </div>
          {wt?.flagged?.length > 0 && (
            <div className="mt-4 space-y-1.5" data-testid="watchtower-flagged">
              {wt.flagged.slice(0, 3).map((f) => (
                <div key={f.supplier_id} className="flex items-center justify-between border-l-2 border-red-400 bg-red-50/60 px-2.5 py-1.5 text-xs">
                  <span className="font-medium text-ek-ink">{f.legal_name}</span>
                  <span className="text-red-600">{f.match_count} match(es) · {f.country}</span>
                </div>
              ))}
            </div>
          )}
          {history.length > 0 && (
            <div className="mt-4" data-testid="watchtower-history">
              <p className="ek-eyebrow mb-1.5 text-[0.6rem]">Recent runs</p>
              <div className="space-y-1">
                {history.map((h, i) => (
                  <div key={i} className="flex items-center justify-between text-[11px] text-ek-ink/65">
                    <span>{new Date(h.run_at).toLocaleString()}</span>
                    <span>
                      {h.screened} screened ·{" "}
                      <span className={h.hits > 0 ? "font-semibold text-red-600" : ""}>{h.hits} hit(s)</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* GDELT feed */}
        <div className="border border-black/5 bg-white p-5" data-testid="gdelt-card">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-1.5 font-display text-base font-semibold text-ek-ink">
              <Newspaper size={16} className="text-ek-purple" /> GDELT threat feed
            </h3>
            {isAdmin && (
              <button
                onClick={pullGdelt}
                disabled={pulling}
                data-testid="gdelt-pull-button"
                className="flex items-center gap-1 rounded-sm border border-ek-purple/30 px-2.5 py-1 text-xs font-semibold text-ek-purple transition-colors hover:bg-ek-purple/5 disabled:opacity-50"
              >
                <RefreshCw size={11} className={pulling ? "animate-spin" : ""} /> Pull
              </button>
            )}
          </div>
          <p className="mt-1 text-xs text-ek-ink/65">
            Live open-source risk news (auto-ingested every 6 hours).
          </p>
          {canReadSignals && trend.length > 0 && (
            <div className="mt-3" data-testid="gdelt-trend">
              <div className="flex h-12 items-end gap-0.5">
                {trend.map((d) => {
                  const max = Math.max(1, ...trend.map((x) => x.count));
                  const h = d.count === 0 ? 2 : Math.max(4, (d.count / max) * 44);
                  return (
                    <div
                      key={d.date}
                      title={`${d.date}: ${d.count}`}
                      style={{ height: `${h}px` }}
                      className={`flex-1 rounded-sm ${d.count > 0 ? "bg-ek-purple/60" : "bg-black/10"}`}
                    />
                  );
                })}
              </div>
              <p className="mt-1 text-[10px] text-ek-ink/35">Last 14 days · ingestion volume</p>
            </div>
          )}
          {!canReadSignals ? (
            <p className="mt-4 text-xs text-ek-ink/65">Signal-read permission required to view the feed.</p>
          ) : feed.length === 0 ? (
            <div className="mt-4 flex flex-col items-center gap-2 py-6 text-ek-ink/65">
              <Radar size={24} className="text-ek-purple/40" />
              <p className="text-xs">No GDELT articles yet — pull to populate.</p>
            </div>
          ) : (
            <div className="mt-4 space-y-2" data-testid="gdelt-feed">
              {feed.map((s) => {
                const original = s.raw?.title_original;
                const showingOrig = showOrig.has(s.signal_id);
                return (
                  <div key={s.signal_id} className="flex items-start gap-2 border-b border-black/5 pb-2 last:border-0">
                    <SeverityBadge severity={s.severity} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs text-ek-ink/80" title={showingOrig ? original : tx(s.summary)}>
                        {showingOrig ? original : tx(s.summary)}
                      </p>
                      <p className="flex items-center gap-1.5 text-[10px] text-ek-ink/65">
                        <span>{s.raw?.domain || s.subject?.name} · {ago(s.received_at)}</span>
                        {original && (
                          <button
                            onClick={() => toggleOrig(s.signal_id)}
                            data-testid={`gdelt-orig-toggle-${s.signal_id}`}
                            className="font-semibold text-ek-purple/70 transition-colors hover:text-ek-purple"
                            title={showingOrig ? "Show translated headline" : "Show the original-language headline"}
                          >
                            {showingOrig ? "· show translated" : "· view original"}
                          </button>
                        )}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const Stat = ({ label, value, accent, small }) => (
  <div className="bg-ek-canvas px-3 py-2">
    <p className={`font-display font-bold ${small ? "text-sm" : "text-2xl"} ${accent ? "text-red-600" : "text-ek-ink"}`}>
      {value}
    </p>
    <p className="text-[10px] uppercase tracking-wide text-ek-ink/65">{label}</p>
  </div>
);
